import React, { ReactNode, useEffect, useState }               from 'react';
import { Header, Icon, IconProps, Modal, ModalProps, Segment } from "semantic-ui-react";
import _                                                       from "underscore";
import { Cell, Grid }                                          from "styled-css-grid";
import classNames                                              from "classnames";

import { SemanticUIColor, SemanticUISize }                from "../../../types";
import { CorvalLogo, CorvalLogoVersions }                 from "../../components/product/CorvalLogo";
import { BasicDialog, BasicDialogProps }                  from "../modals/BasicDialog";
import { BasicDialogContainer }                           from "../modals/BasicDialogContainer";
import { CHARACTER_VISUAL_AID, CorvalCharacterVisualAid } from "../product/CorvalCharacterVisualAid";
import { FinePrintMessage }                               from "../util/messaging/FinePrintMessage";


export type DecisionPointDefinition = {

    // Title for the decision point
    title: string,

    // Brief description of the decision point
    description?: ReactNode,

    // What icon to use for the decision point
    icon: Omit<IconProps, 'size' | 'color'>,

    // Details to show when the decision point is selected
    details: ReactNode,

    // The label of the perceived action to take when the decision point is selected
    actionLabel: string,

    // Is this decision point available and selectable?
    active: boolean,

    // Is this decision point available or not?
    available: boolean,
}

interface DecisionPointDialogProps<T = any> {
    isOpen?: boolean,
    dialogTitle?: string,
    dialogProps?: BasicDialogProps,
    modalProps?: ModalProps,
    decisionOptions?: Map<T, DecisionPointDefinition>,
    innerContainerOnly?: boolean,
    contentGridColumnWidth?: string,
    visualAidType?: CHARACTER_VISUAL_AID,

    onDecisionSelect?( selectionType: T ): void,
}

const DecisionPointDialogDefaultProps = {
    isOpen:                 false,
    innerContainerOnly:     false,
    dialogTitle:            `What do you want to do?`,
    dialogProps:            {} as BasicDialogProps,
    modalProps:             {} as ModalProps,
    decisionOptions:        new Map<'Yes' | 'No', DecisionPointDefinition>( [
        [ 'Yes', {
            title:       'Yes',
            actionLabel: `Let's Go`,
            active:      true,
            available:   true,
            details:     <>You chose "Yes".</>,
            description: null,
            icon:        { name: 'check circle' }
        } ],
        [ 'No', {
            title:       'No',
            actionLabel: `Let's Go`,
            active:      true,
            available:   true,
            details:     <>You chose "No".</>,
            description: null,
            icon:        { name: 'close' }
        } ],
    ] ),
    contentGridColumnWidth: `25%`,
    visualAidType:          CHARACTER_VISUAL_AID.CORVAL_HEX_HELPING_USER,
    onDecisionSelect:       ( selectionType ) => {},
} satisfies Partial<DecisionPointDialogProps>

function DecisionPointDialog<T>( p: DecisionPointDialogProps<T> ){

    const props = { ...DecisionPointDialogDefaultProps, ...p };

    const [ selectedType, setSelectedType ] = useState( null );

    useEffect( () => {

        props.onDecisionSelect( selectedType );

    }, [ selectedType ] );

    const handleOptionSelected = ( e: MouseEvent, selectionType: T ) => {

        setSelectedType( selectionType );
    }

    const WrapperComponent = ( { children } ) => ( props.innerContainerOnly )
                                                 ? <>{ children }</>
                                                 : <Modal open={ props.isOpen } size='large'{ ...props.modalProps } onUnmount={ () => setSelectedType( null ) }>{ children }</Modal>;

    return (

        <WrapperComponent>

            <BasicDialog
                hasSystemContext={ false }
                submitLabel={ _.isNull( selectedType ) ? null : props.decisionOptions.get( selectedType ).actionLabel }
                showSubmitButton={ ( !_.isNull( selectedType ) && !_.isNull( props.decisionOptions.get( selectedType ).actionLabel ) ) }
                cancelLabel={ 'Close' }
                className={ 'no-padding' }
                onSubmit={ () => ( { selectedType } ) }
                { ...props.dialogProps }>

                <BasicDialogContainer title={ props.dialogTitle }>

                    <Grid columns={ `20% 1fr ${ props.contentGridColumnWidth }` } columnGap={ '35px' }>
                        <Cell middle center>
                            <CorvalCharacterVisualAid fluid={ true } type={ props.visualAidType }/>
                        </Cell>
                        <Cell middle>
                            <Grid columns={ 1 } rowGap={ '10px' }>

                                { Array.from( props.decisionOptions as any )
                                       .filter( ( [ selectionType, selectionTypeInfo ]: [ T, DecisionPointDefinition ] ) => selectionTypeInfo.available )
                                       .map( ( [ selectionType, selectionTypeInfo ]: [ T, DecisionPointDefinition ] ) => {

                                           const isSelected = ( selectionType === selectedType ),
                                                 isActive   = selectionTypeInfo.active;

                                           return (
                                               <Cell middle center key={ selectionType as any }>

                                                   <Segment onClick={ e => isActive ? handleOptionSelected( e, selectionType ) : null }
                                                            disabled={ !isActive }
                                                            className={ classNames( {
                                                                'cursor--pointer':         isActive,
                                                                'cursor--prohibited':      !isActive,
                                                                'text-left':               true,
                                                                'outline-md':              isActive && isSelected,
                                                                'outline-sm':              isActive && !isSelected,
                                                                'outline--colored-purple': isActive,
                                                                'outline--hover':          isActive && !isSelected,
                                                            } ) }>

                                                       <Grid columns={ `auto 1fr` } columnGap={ '10px' }>
                                                           <Cell middle>
                                                               <Icon { ...selectionTypeInfo.icon }
                                                                     size={ SemanticUISize.LARGE }
                                                                     color={ isSelected ? SemanticUIColor.PURPLE : SemanticUIColor.GREY }
                                                               />
                                                           </Cell>
                                                           <Cell middle>
                                                               <Header size={ 'small' } className={ 'no-margin' }>{ selectionTypeInfo.title }</Header>
                                                               { selectionTypeInfo.description && <FinePrintMessage fadeIn={ false }>{ selectionTypeInfo.description }</FinePrintMessage> }
                                                           </Cell>
                                                       </Grid>

                                                   </Segment>

                                               </Cell>
                                           );

                                       } ) }
                            </Grid>
                        </Cell>
                        <Cell middle>

                            { _.isNull( selectedType )
                              ? (
                                  <Grid columns={ 1 } rows={ `auto auto` } rowGap={ '10px' } className={ 'text-center margin-lg-horizontal' }>
                                      <Cell middle center style={ { display: 'grid', placeItems: 'center' } }>
                                          <div className={ 'display--inline-block pulse-grey bg--colored-grey' } style={ { borderRadius: '50%', width: 80, height: 80 } }>
                                              <CorvalLogo size={ 'tiny' } type={ CorvalLogoVersions.WHITE_HEX_LOGO } style={ { transform: 'scale(0.8)' } }/>
                                          </div>
                                      </Cell>
                                      <Cell middle center><FinePrintMessage>Choose an option for more information on how to proceed.</FinePrintMessage></Cell>
                                  </Grid>
                              )
                              : <>{ props.decisionOptions.get( selectedType ).details }</>
                            }

                        </Cell>
                    </Grid>

                </BasicDialogContainer>

            </BasicDialog>

        </WrapperComponent>
    );
}

export {
    DecisionPointDialog,
    DecisionPointDialogDefaultProps,
    type DecisionPointDialogProps,
};
